/**
 * Reporting queries — dual database paths (web Postgres vs native SQLite).
 *
 * Aggregate stats (player counts, last played, new players, etc.) cannot run
 * through PostgREST. Each report therefore has two SQL implementations that
 * must stay semantically aligned:
 *
 * 1. **Web / browser** (`tauri.enabled === false`):
 *    Delegates to `actions.reporting.*`, which calls the `reporting` edge
 *    function. SQL runs on Postgres in `supabase/functions/reporting/`.
 *    Requires network. RLS applies via JWT claim set in each action handler.
 *
 * 2. **Tauri / native desktop** (`tauri.enabled === true`):
 *    Runs equivalent SQL locally against the synced SQLite database via
 *    `plugin:mixitonedb|execute`. Works offline. No edge function round trip.
 *
 * **Callers must import from this module** (`lib/reporting`), not from
 * `@mixitone/actions` directly, so the correct path is chosen at runtime.
 *
 * **Adding or changing a report**
 * - Add or edit the Postgres action in `supabase/functions/reporting/<name>.ts`
 *   and register it in `supabase/functions/reporting/index.ts`.
 * - Run `pnpm run gen:functions` to refresh action typings.
 * - Add a matching wrapper here with the SQLite query in the `tauri.enabled`
 *   branch and `return reporting.<name>(...args)` in the else branch.
 * - Keep the same params, response shape, and business meaning on both paths.
 *   See `docs/reporting.md` for SQLite vs Postgres conventions in this repo.
 *
 * **Similar pattern:** `packages/mixitone/src/lib/globalSearch.ts`.
 */
import { actions } from "@mixitone/actions";
import tauri from "./tauri";

const reporting = actions.reporting;

export async function clubPlayerLastPlayed(
  ...args: Parameters<typeof reporting.clubPlayerLastPlayed>
): Promise<ReturnType<typeof reporting.clubPlayerLastPlayed>> {
  if (tauri.enabled) {
    const sql = `
        SELECT 
            p.club_player_id,
            COUNT(DISTINCT n.id) as nights_played,
            MIN(n.date) as date_of_first_night_played,
            MAX(n.date) as date_of_last_night_played
        FROM 
            players p
            JOIN nights n ON p.night_id = n.id
        WHERE
            p.is_deleted = false
            AND n.is_deleted = false
        GROUP BY 
            p.club_player_id;
    `;
    return {
      clubPlayersLastPlayed: await tauri.invoke("plugin:mixitonedb|execute", { query: sql, values: [] }),
    };
  }

  return reporting.clubPlayerLastPlayed(...args);
}

export async function playersPerNight(
  ...args: Parameters<typeof reporting.playersPerNight>
): Promise<ReturnType<typeof reporting.playersPerNight>> {
  if (tauri.enabled) {
    const params = args[0];
    const { clubId, startDate, endDate } = params;

    const sql = `
        WITH previous_players AS (
          SELECT DISTINCT
            p.club_player_id,
            MIN(n.date) as first_appearance_date
          FROM
            nights n
            JOIN players p ON n.id = p.night_id
          WHERE
            n.is_deleted = 'false'
            AND p.is_deleted = 'false'
            AND n.club_id = ?
          GROUP BY
            p.club_player_id
        )
        SELECT
            n.id,
            n.date,
            COUNT(DISTINCT p.id) as players_per_night,
            COUNT(DISTINCT CASE 
                WHEN pp.first_appearance_date = n.date THEN p.club_player_id 
                ELSE NULL 
            END) as new_players_count
        FROM
            nights n
            JOIN players p ON n.id = p.night_id
            LEFT JOIN previous_players pp ON p.club_player_id = pp.club_player_id
        WHERE
            n.is_deleted = 'false'
            AND p.is_deleted = 'false'
            AND n.club_id = ?
            AND date(n.date) >= date(?)
            AND date(n.date) < date(?, '+1 day')
        GROUP BY
            n.id
        ORDER BY
            n.date;
    `;

    return {
      playersPerNight: await tauri.invoke("plugin:mixitonedb|execute", {
        query: sql,
        values: [clubId, clubId, startDate, endDate],
      }),
    };
  }

  return reporting.playersPerNight(...args);
}

export async function sessionNewPlayers(
  ...args: Parameters<typeof reporting.sessionNewPlayers>
): Promise<ReturnType<typeof reporting.sessionNewPlayers>> {
  if (tauri.enabled) {
    const params = args[0];
    const { sessionId } = params;

    const sql = `
        WITH previous_players AS (
          SELECT DISTINCT
            p.club_player_id,
            MIN(n.date) as first_appearance_date
          FROM
            nights n
            JOIN players p ON n.id = p.night_id
          WHERE
            n.is_deleted = 'false'
            AND p.is_deleted = 'false'
          GROUP BY
            p.club_player_id
        )
        SELECT DISTINCT
          p.club_player_id
        FROM
          nights n
          JOIN players p ON n.id = p.night_id
          JOIN previous_players pp ON p.club_player_id = pp.club_player_id
        WHERE
          n.is_deleted = 'false'
          AND p.is_deleted = 'false'
          AND n.id = ?
          AND pp.first_appearance_date = n.date;
    `;

    const results = await tauri.invoke<Array<{ club_player_id: string }>>("plugin:mixitonedb|execute", {
      query: sql,
      values: [sessionId],
    });

    return {
      clubPlayerIds: results.map((r) => r.club_player_id),
    };
  }

  return reporting.sessionNewPlayers(...args);
}
